import { useMemo } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useAppStore } from '@/stores/useAppStore';
import { useAuthStore } from '@/stores/useAuthStore';
import { PILLAR_INFO } from '@/lib/seeds';
import { getTodayISO } from '@/lib/constants';
import { Flame, Clock, Target, TrendingUp } from 'lucide-react';
import * as Icons from 'lucide-react';
import { toast } from 'sonner';
import { PillarSlug } from '@/types';

interface TodayProgressSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TodayProgressSheet({ open, onOpenChange }: TodayProgressSheetProps) {
  const { user } = useAuthStore();
  const { habits, challenge, toggleHabitCheck, checkChallengeDay } = useAppStore();
  const today = getTodayISO();
  const todayDate = new Date();
  const dayOfWeek = todayDate.getDay(); // 0 = Sunday
  const dayOfMonth = todayDate.getDate();

  // Get all activities for today
  const todayActivities = useMemo(() => {
    const activities: Array<{
      id: string;
      type: 'habit' | 'challenge';
      title: string;
      pillars?: string[];
      completed: boolean;
      time?: string;
      streak?: number;
    }> = [];

    // Add challenge if active
    if (challenge && challenge.status === 'ativo') {
      const isCompleted = challenge?.completedDates?.includes(today) ?? false;
      activities.push({
        id: 'challenge',
        type: 'challenge',
        title: challenge.habitTitle,
        completed: isCompleted,
        time: challenge.hour,
        streak: challenge.streak,
      });
    }

    // Add habits for today
    habits?.forEach((habit) => {
      if (!habit.active) return;

      let shouldShow = false;

      if (habit.frequency === 'daily') {
        shouldShow = true;
      } else if (habit.frequency === 'weekly' && habit.daysOfWeek) {
        shouldShow = habit.daysOfWeek.includes(dayOfWeek);
      } else if (habit.frequency === 'monthly' && habit.dayOfMonth) {
        shouldShow = habit.dayOfMonth === dayOfMonth;
      }

      if (shouldShow) {
      activities.push({
        id: habit.id,
        type: 'habit',
        title: habit.title,
        pillars: habit.pillars || (habit.pillar ? [habit.pillar] : []),
        completed: habit.history.includes(today),
        time: habit.hour,
      });
      }
    });

    // Sort by time, then by completion status
    activities.sort((a, b) => {
      if (a.time && b.time) {
        return a.time.localeCompare(b.time);
      }
      if (a.completed !== b.completed) {
        return a.completed ? 1 : -1;
      }
      return 0;
    });

    return activities;
  }, [habits, challenge, today, dayOfWeek, dayOfMonth]);

  const completedCount = todayActivities.filter(a => a.completed).length;
  const totalCount = todayActivities.length;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const handleToggle = async (activity: typeof todayActivities[0]) => {
    try {
      if (activity.type === 'challenge' && challenge) {
        await checkChallengeDay(challenge.id, today);
      } else {
        await toggleHabitCheck(activity.id, today);
      }
      toast.success('✅ Check-in salvo!');
    } catch (error) {
      console.error('Erro ao salvar check-in:', error);
      // O erro já foi tratado no store
    }
  };

  const groupedByPillar = useMemo(() => {
    const grouped: Record<string, typeof todayActivities> = {
      challenge: [],
      ...Object.fromEntries((user?.pilares_foco || []).map(p => [p, []])),
    };

    todayActivities.forEach(activity => {
      if (activity.type === 'challenge') {
        grouped.challenge.push(activity);
      } else if (activity.pillars && activity.pillars.length > 0) {
        // Adicionar atividade em CADA pilar associado
        activity.pillars.forEach(pillarSlug => {
          if (!grouped[pillarSlug]) {
            grouped[pillarSlug] = [];
          }
          // Evitar duplicatas no mesmo grupo
          if (!grouped[pillarSlug].some(a => a.id === activity.id)) {
            grouped[pillarSlug].push(activity);
          }
        });
      }
    });

    return grouped;
  }, [todayActivities, user?.pilares_foco]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="right" 
        className="w-full sm:max-w-xl overflow-y-auto z-[10001]"
      >
        <SheetHeader>
          <SheetTitle className="text-2xl">Progresso de Hoje</SheetTitle>
          <SheetDescription>
            {new Date().toLocaleDateString('pt-BR', { 
              weekday: 'long', 
              day: 'numeric', 
              month: 'long' 
            })}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Overall Progress */}
          <div className="p-4 rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Target className="h-5 w-5 text-primary" />
                <span className="font-semibold">Progresso Geral</span>
              </div>
              <span className="text-3xl font-bold text-primary">{progressPercent}%</span>
            </div>
            <Progress value={progressPercent} className="h-3 mb-2" />
            <p className="text-sm text-muted-foreground">
              {completedCount} de {totalCount} atividades concluídas
            </p>
          </div>

          {/* Statistics */}
          {totalCount > 0 && (
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-muted/50 border">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="h-4 w-4 text-success" />
                  <span className="text-xs text-muted-foreground">Completas</span>
                </div>
                <div className="text-2xl font-bold">{completedCount}</div>
              </div>
              <div className="p-3 rounded-lg bg-muted/50 border">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="h-4 w-4 text-warning" />
                  <span className="text-xs text-muted-foreground">Pendentes</span>
                </div>
                <div className="text-2xl font-bold">{totalCount - completedCount}</div>
              </div>
            </div>
          )}

          {/* No activities */}
          {totalCount === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <p>Nenhuma atividade programada para hoje</p>
              <p className="text-sm mt-2">Que tal criar um novo hábito?</p>
            </div>
          )}

          {/* Challenge Section */}
          {groupedByPillar.challenge.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Flame className="h-5 w-5 text-warning" />
                <h3 className="font-semibold text-lg">Desafio 90 Dias</h3>
              </div>
              {groupedByPillar.challenge.map(activity => (
                <ActivityItem key={activity.id} activity={activity} onToggle={handleToggle} />
              ))}
            </div>
          )}

          {/* Pillar Sections */}
          {Object.entries(groupedByPillar).map(([pillarSlug, activities]) => {
            if (pillarSlug === 'challenge' || activities.length === 0) return null;
            
            const pillarInfo = PILLAR_INFO[pillarSlug];
            if (!pillarInfo) return null;

            const iconName = pillarInfo.icon.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('');
            const IconComponent = Icons[iconName as keyof typeof Icons] as any || Icons.Activity;

            return (
              <div key={pillarSlug} className="space-y-3">
                <div className="flex items-center gap-2">
                  <div 
                    className="flex h-8 w-8 items-center justify-center rounded-lg"
                    style={{ backgroundColor: pillarInfo.lightColor }}
                  >
                    <IconComponent className="h-4 w-4" style={{ color: pillarInfo.color }} />
                  </div>
                  <h3 className="font-semibold text-lg">{pillarInfo.name}</h3>
                </div>
                {activities.map(activity => (
                  <ActivityItem key={activity.id} activity={activity} onToggle={handleToggle} />
                ))}
              </div>
            );
          })}

          {/* Success Message */}
          {completedCount === totalCount && totalCount > 0 && (
            <div className="p-4 rounded-lg bg-gradient-to-br from-success/10 to-success/5 border border-success/20 text-center">
              <div className="text-4xl mb-2">🎉</div>
              <p className="font-semibold text-success mb-1">
                Parabéns! Todas as atividades concluídas!
              </p>
              <p className="text-sm text-muted-foreground">
                Você está construindo uma vida extraordinária!
              </p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ActivityItem({ 
  activity, 
  onToggle 
}: { 
  activity: any; 
  onToggle: (activity: any) => void;
}) {
  return (
    <div 
      className={`
        p-4 rounded-lg border-2 transition-all
        ${activity.completed 
          ? 'bg-success/5 border-success/50' 
          : 'bg-card border-border hover:border-primary/50'
        }
      `}
    >
      <div className="flex items-start gap-3">
        <Checkbox
          checked={activity.completed}
          onCheckedChange={() => onToggle(activity)}
          className="mt-1"
        />
        <div className="flex-1 min-w-0">
          <div className={`font-medium ${activity.completed ? 'line-through text-muted-foreground' : ''}`}>
            {activity.title}
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {activity.pillars && activity.pillars.length > 0 && (
              <>
                {activity.pillars.slice(0, 2).map((pillarSlug: string) => {
                  const pillar = PILLAR_INFO[pillarSlug as PillarSlug];
                  if (!pillar) return null;
                  return (
                    <Badge key={pillarSlug} variant="secondary" className="text-xs">
                      {pillar.icon} {pillar.name}
                    </Badge>
                  );
                })}
                {activity.pillars.length > 2 && (
                  <Badge variant="outline" className="text-xs">
                    +{activity.pillars.length - 2}
                  </Badge>
                )}
              </>
            )}
            {activity.time && (
              <Badge variant="outline" className="text-xs">
                <Clock className="h-3 w-3 mr-1" />
                {activity.time}
              </Badge>
            )}
            {activity.type === 'challenge' && activity.streak !== undefined && (
              <Badge variant="outline" className="text-xs">
                <Flame className="h-3 w-3 mr-1" />
                {activity.streak} dias de sequência
              </Badge>
            )}
            {activity.completed && (
              <Badge variant="outline" className="text-xs bg-success/10 text-success border-success/50">
                ✓ Concluído
              </Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
